/**
 * BasicInfoSection Component
 * 협력사 기본 정보 표시 섹션
 */
import React, { useEffect, useRef, useState } from 'react';
import { SupplierInfo } from '../types/supplierInfo.types';
import { SaveIcon } from '@/shared/components/icons/Icons';
import { getItem } from '@/shared/utils/storage';
import { commonCodeApi } from '@/shared/api/commonCodeApi';
import { CommonCodeDto } from '@/shared/types/commonCode.types';

interface BasicInfoSectionProps {
    supplierInfo: SupplierInfo;
    saving: boolean;
    onFieldChange: (field: keyof SupplierInfo, value: string) => void;
    onSave: () => void;
}

const BasicInfoSection: React.FC<BasicInfoSectionProps> = ({ supplierInfo, saving, onFieldChange, onSave }) => {
    // 사용자 타입 확인 (관리자만 협력사 타입 수정 가능)
    const userType = getItem<string>('USER_TYPE');
    const isAdmin = userType === 'HEADQUARTERS';

    const [gradeCodes, setGradeCodes] = useState<CommonCodeDto[]>([]);
    const hasFetchedGradeCodes = useRef(false);

    useEffect(() => {
        if (hasFetchedGradeCodes.current) return;
        let isCancelled = false;
        const fetchGradeCodes = async () => {
            try {
                const response = await commonCodeApi.getCommonCodeList('SUPP_GRADE');
                if (!isCancelled) {
                    setGradeCodes(response.codes || []);
                    hasFetchedGradeCodes.current = true;
                }
            } catch {
                // 등급 코드 조회 실패 시 기본값 유지
            }
        };
        fetchGradeCodes();
        return () => {
            isCancelled = true;
        };
    }, []);

    return (
        <div className="supplier-section">
            <h2 className="section-title">기본 정보</h2>

            {/* Row 1: 협력사명, 협력사 아이디, 협력사 타입 */}
            <div className="supplier-form-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                <div className="form-group">
                    <label className="form-label">협력사명</label>
                    <div className="form-display">{supplierInfo.supplierName || '-'}</div>
                </div>
                <div className="form-group">
                    <label className="form-label">협력사 아이디</label>
                    <div className="form-display">{supplierInfo.supplierId || '-'}</div>
                </div>
                <div className="form-group">
                    <label className="form-label">협력사 타입</label>
                    {isAdmin ? (
                        <select
                            className="form-select"
                            value={
                                supplierInfo.suppType === '가맹'
                                    ? 'F'
                                    : supplierInfo.suppType === '직영'
                                      ? 'D'
                                      : supplierInfo.suppType === 'F'
                                        ? 'F'
                                        : supplierInfo.suppType === 'D'
                                          ? 'D'
                                          : 'D'
                            }
                            onChange={(e) => onFieldChange('suppType', e.target.value)}
                        >
                            <option value="D">직영</option>
                            <option value="F">가맹</option>
                        </select>
                    ) : (
                        <div className="form-display">{supplierInfo.suppType || '-'}</div>
                    )}
                </div>
            </div>

            {/* Row 2: 협력사 등급, 표준권역, 협력사 세트수 */}
            <div
                className="supplier-form-grid"
                style={{ gridTemplateColumns: 'repeat(3, 1fr)', marginTop: 'var(--spacing-4)' }}
            >
                <div className="form-group">
                    <label className="form-label">협력사 등급</label>
                    {isAdmin ? (
                        <select
                            className="form-select"
                            value={supplierInfo.suppLvl || ''}
                            onChange={(e) => onFieldChange('suppLvl', e.target.value)}
                        >
                            <option value="">선택하세요</option>
                            {gradeCodes.map((grade) => (
                                <option key={grade.comCode} value={grade.comCode}>
                                    {grade.comCdName}
                                </option>
                            ))}
                        </select>
                    ) : (
                        <div className="form-display">{supplierInfo.supplierGrade || '-'}</div>
                    )}
                </div>
                <div className="form-group">
                    <label className="form-label">표준권역</label>
                    <div className="form-display">{supplierInfo.standardArea || '-'}</div>
                </div>
                <div className="form-group">
                    <label className="form-label">협력사 세트수</label>
                    {isAdmin ? (
                        <input
                            type="number"
                            className="form-input"
                            value={supplierInfo.slotSetCnt || ''}
                            onChange={(e) => onFieldChange('slotSetCnt', e.target.value)}
                            placeholder="세트수를 입력하세요"
                            min="0"
                        />
                    ) : (
                        <div className="form-display">{supplierInfo.slotSetCnt || '-'}</div>
                    )}
                </div>
            </div>

            {/* Row 3: 상태, 등록일, 운영종료일 */}
            <div
                className="supplier-form-grid"
                style={{ gridTemplateColumns: 'repeat(3, 1fr)', marginTop: 'var(--spacing-4)' }}
            >
                <div className="form-group">
                    <label className="form-label">상태</label>
                    <div className="form-display">{supplierInfo.status || '-'}</div>
                </div>
                <div className="form-group">
                    <label className="form-label">등록일</label>
                    <div className="form-display">{supplierInfo.registrationDate || '-'}</div>
                </div>
                <div className="form-group">
                    <label className="form-label">운영종료일</label>
                    <div className="form-display">{supplierInfo.operationEndDate || '-'}</div>
                </div>
            </div>

            {/* Row 4: 배달권역, 전화번호, 협력사 주소 */}
            <div
                className="supplier-form-grid"
                style={{ gridTemplateColumns: 'repeat(3, 1fr)', marginTop: 'var(--spacing-4)' }}
            >
                <div className="form-group">
                    <label className="form-label">배달권역</label>
                    <div className="form-display">{supplierInfo.deliveryArea || '-'}</div>
                </div>
                <div className="form-group">
                    <label className="form-label">전화번호</label>
                    <input
                        type="text"
                        className="form-input"
                        value={supplierInfo.mobilePhone || ''}
                        onChange={(e) => onFieldChange('mobilePhone', e.target.value)}
                        placeholder="휴대폰 번호를 입력하세요"
                    />
                </div>
                <div className="form-group">
                    <label className="form-label">협력사 주소</label>
                    <input
                        type="text"
                        className="form-input"
                        value={supplierInfo.address || ''}
                        onChange={(e) => onFieldChange('address', e.target.value)}
                        placeholder="협력사 주소를 입력하세요"
                    />
                </div>
            </div>

            {/* 저장 버튼 */}
            <div className="section-actions">
                <button className="btn btn-primary" onClick={onSave} disabled={saving}>
                    <SaveIcon width={16} height={16} />
                    <span>{saving ? '저장 중...' : '저장'}</span>
                </button>
            </div>
        </div>
    );
};

export default BasicInfoSection;
